<template>
  <div class="notification-bell">
    <button
      class="bell-btn"
      :class="{ 'has-alerts': hasAlerts }"
      :title="bellTitle"
      @click="toggle"
    >
      <i class="bi" :class="hasAlerts ? 'bi-bell-fill' : 'bi-bell'"></i>
      <span v-if="hasAlerts" class="bell-badge" :class="{ critical: expiredCount > 0 }">
        {{ alerts.length }}
      </span>
    </button>

    <div v-if="open" class="notification-dropdown">
      <div class="dropdown-header">
        <span class="dropdown-title">Notifications</span>
        <span v-if="hasAlerts" class="dropdown-count">{{ alerts.length }}</span>
      </div>

      <div v-if="!hasAlerts" class="dropdown-empty">
        <i class="bi bi-check2-circle"></i>
        <span>You're all caught up</span>
      </div>

      <ul v-else class="notification-list">
        <li
          v-for="alert in alerts"
          :key="alert.id"
          class="notification-item"
          :class="alert.status"
          @click="goToVehicles"
        >
          <div class="item-icon">
            <i class="bi" :class="alert.status === 'expired' ? 'bi-exclamation-octagon-fill' : 'bi-exclamation-triangle-fill'"></i>
          </div>
          <div class="item-body">
            <div class="item-title">
              {{ alert.vehicleName }}
              <span v-if="alert.registrationNumber" class="item-rego">{{ alert.registrationNumber }}</span>
            </div>
            <div class="item-text">
              Registration {{ alert.status === 'expired' ? 'expired' : 'expires' }} {{ formatDate(alert.registrationExpiry) }}
            </div>
            <div class="item-meta" :class="alert.status">{{ alert.label }}</div>
          </div>
        </li>
      </ul>

      <div v-if="hasAlerts" class="dropdown-footer">
        <button class="footer-btn" @click="goToVehicles">
          <i class="bi bi-truck"></i>
          Manage vehicles
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useVehicleAlerts } from '@/composables/useVehicleAlerts'

// Admin sessions stay open for a long time — re-check periodically so a rego
// that lapses overnight shows up without a page reload.
const REFRESH_INTERVAL_MS = 30 * 60 * 1000

export default {
  name: 'NotificationBell',
  setup() {
    const { alerts, hasAlerts, expiredCount, fetchVehicleAlerts } = useVehicleAlerts()
    return { alerts, hasAlerts, expiredCount, fetchVehicleAlerts }
  },
  data() {
    return {
      open: false,
      refreshTimer: null
    }
  },
  computed: {
    bellTitle() {
      if (!this.hasAlerts) return 'No notifications'
      const n = this.alerts.length
      return `${n} vehicle ${n === 1 ? 'registration needs' : 'registrations need'} attention`
    }
  },
  methods: {
    toggle() {
      this.open = !this.open
    },
    close() {
      this.open = false
    },
    goToVehicles() {
      this.open = false
      if (this.$route.path !== '/vehicles') {
        this.$router.push('/vehicles')
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return this.$dayjs(dateString).format('D MMM YYYY')
    },
    handleDocumentClick(event) {
      if (this.open && !this.$el.contains(event.target)) {
        this.close()
      }
    }
  },
  mounted() {
    this.fetchVehicleAlerts()
    document.addEventListener('click', this.handleDocumentClick)
    this.refreshTimer = setInterval(() => {
      this.fetchVehicleAlerts({ force: true })
    }, REFRESH_INTERVAL_MS)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick)
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  }
}
</script>

<style scoped>
.notification-bell {
  position: relative;
  display: flex;
  align-items: center;
}

.bell-btn {
  position: relative;
  background: none;
  border: none;
  color: #e0f7ef;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
}

.bell-btn:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.12);
}

.bell-btn i {
  font-size: 1.25rem;
}

.bell-btn.has-alerts i {
  color: #fff;
}

.bell-badge {
  position: absolute;
  top: 0.125rem;
  right: 0.125rem;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9999px;
  background-color: #d97706;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 0 0 2px var(--primary);
}

.bell-badge.critical {
  background-color: #dc2626;
}

.notification-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  width: 360px;
  max-width: calc(100vw - 2rem);
  background: #fff;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-xl);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border-light);
  background-color: #f9fafb;
}

.dropdown-title {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-dark);
}

.dropdown-count {
  min-width: 20px;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background-color: #fee2e2;
  color: #991b1b;
  font-size: 0.6875rem;
  font-weight: 700;
  text-align: center;
}

.dropdown-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.dropdown-empty i {
  font-size: 1.75rem;
  color: #10b981;
}

.notification-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 380px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border-light);
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-left: 3px solid transparent;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover {
  background-color: #f9fafb;
}

.notification-item.expiring {
  border-left-color: #d97706;
}

.notification-item.expired {
  border-left-color: #dc2626;
}

.item-icon i {
  font-size: 1.125rem;
}

.notification-item.expiring .item-icon i {
  color: #d97706;
}

.notification-item.expired .item-icon i {
  color: #dc2626;
}

.item-body {
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
  min-width: 0;
}

.item-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-dark);
}

.item-rego {
  padding: 0.0625rem 0.375rem;
  border-radius: 4px;
  background-color: #f3f4f6;
  color: #374151;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.item-text {
  font-size: 0.8125rem;
  color: #6b7280;
}

.item-meta {
  font-size: 0.75rem;
  font-weight: 600;
}

.item-meta.expiring {
  color: #b45309;
}

.item-meta.expired {
  color: #991b1b;
}

.dropdown-footer {
  padding: 0.625rem 1rem;
  border-top: 1px solid var(--border-light);
  background-color: #f9fafb;
}

.footer-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--primary);
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.375rem;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.footer-btn:hover {
  background-color: var(--primary-light);
}

@media (max-width: 768px) {
  .notification-dropdown {
    width: 300px;
    /* Must stay 0: the bell is the right-most element in the header, so any
       negative offset pushes the panel past the viewport edge. */
    right: 0;
  }
}
</style>
